import { Environment, Gltf, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import React from "react";

import { type ThreeDeeEmbed } from "@reactivated";

type ModelProps = Omit<React.ComponentProps<typeof Gltf>, "rotation"> & {
    rotate_speed?: number;
};

function Model({ rotate_speed = 0, ...props }: ModelProps) {
    const [rotation, setRotation] = React.useState(0);
    const [hovering, setHovering] = React.useState(false);
    const mousePos = React.useRef(0);
    React.useEffect(() => {
        function onMouseOver() {
            setHovering(true);
        }
        function onMouseOut() {
            setHovering(false);
        }
        function onMouseMove(e: MouseEvent) {
            mousePos.current = e.pageX;
        }
        document.addEventListener("mouseover", onMouseOver, { passive: true });
        document.addEventListener("mouseout", onMouseOut, { passive: true });
        document.addEventListener("mousemove", onMouseMove, { passive: true });
        return () => {
            document.removeEventListener("mouseover", onMouseOver);
            document.removeEventListener("mouseout", onMouseOut);
            document.removeEventListener("mousemove", onMouseMove);
        };
    }, []);

    useFrame((three) => {
        const rotateStep = (rotate_speed * Math.PI) / 2048;

        if (hovering) {
            setRotation((r) => {
                const change =
                    ((mousePos.current - three.size.width / 2) * Math.PI) /
                    (2048 * 128);
                const max = rotateStep * 4;
                const min = max * -1;

                // clamp the change between our max and min
                return r + Math.min(Math.max(min, change), max);
            });
        } else {
            setRotation((r) => r + rotateStep);
        }
    });
    return <Gltf rotation={[0, rotation, 0]} {...props} />;
}

export default function ThreejsEmbed(
    props: Omit<ThreeDeeEmbed, "top_text" | "bottom_text" | "model"> & {
        model: string;
        className?: string;
    },
) {
    return (
        <Canvas className={props.className}>
            <PerspectiveCamera
                makeDefault
                position={[
                    props.camera_position?.x ?? 0,
                    props.camera_position?.y ?? -0.05,
                    props.camera_position?.z ?? 0.2,
                ]}
                fov={props.camera_lens?.fov ?? 75}
                near={props.camera_lens?.near ?? 0.1}
                far={props.camera_lens?.far ?? 1000}
            />
            <React.Suspense fallback={null}>
                {props.environment?.file && (
                    <Environment
                        environmentIntensity={
                            props.environment.intensity ?? 0.1
                        }
                        files={props.environment.file.url}
                    />
                )}
                <Model
                    src={props.model}
                    rotate_speed={props.animation?.speed ?? 0}
                />
            </React.Suspense>
        </Canvas>
    );
}
